<template>
  <div class="sent-codes-display">
    <div v-if="loading" class="loading-state">
      <span class="loading-text">טוען...</span>
    </div>

    <div v-else-if="error" class="error-state">
      <span class="error-text">שגיאה</span>
    </div>

    <div v-else-if="codes.length > 0" class="codes-container">
      <div class="codes-list">
        <div
          v-for="(codeData, index) in codes"
          :key="index"
          class="code-item-container"
          :title="`קוד: ${codeData.code} - נשלח: ${formatDateTime(codeData.created_at)}`"
        >
          <div class="code-text">{{ codeData.code }}</div>
          <div class="code-timestamp">📅 {{ formatDateTime(codeData.created_at) }}</div>
        </div>
      </div>
    </div>

    <div v-else class="no-codes">
      <span class="no-codes-text">אין קודים</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SentCodesDisplay',
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      codes: [],
      loading: false,
      error: false
    }
  },
  mounted () {
    this.loadCodes()
  },
  watch: {
    payload: {
      handler (newPayload) {
        if (newPayload && newPayload.id) {
          this.loadCodes()
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async loadCodes () {
      if (!this.payload || !this.payload.id) {
        return
      }

      // Use pre-fetched sent_codes from the main response if available
      if (this.payload.sent_codes) {
        this.codes = this.payload.sent_codes.map(stat => ({
          code: `A-${stat.id}`,
          created_at: stat.created_at
        }))
        return
      }

      // Fallback: fetch individually (for other pages that don't batch)
      this.loading = true
      this.error = false

      try {
        const stats = await this.$network_utils.getStats({
          designator: 'send_cv_modal_opened',
          resource_type: 'Offer',
          resource_id: this.payload.id
        })

        this.codes = stats.map(stat => ({
          code: `A-${stat.id}`,
          created_at: stat.created_at
        }))
      } catch (error) {
        console.error('Error loading codes for offer:', this.payload.id, error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    formatDateTime (dateString) {
      if (!dateString) return ''

      try {
        const date = new Date(dateString)

        // Check if date is valid
        if (isNaN(date.getTime())) return ''

        // Format for Hebrew locale with Israel timezone
        const options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Jerusalem'
        }

        const formatter = new Intl.DateTimeFormat('he-IL', options)
        return formatter.format(date)
      } catch (error) {
        console.error('Error formatting date:', error)
        return ''
      }
    }
  }
}
</script>

<style scoped>
.sent-codes-display {
  min-width: 100px;
  font-size: 12px;
  direction: rtl;
}

.loading-state {
  color: #6c757d;
  font-style: italic;
}

.loading-text {
  font-size: 11px;
}

.error-state {
  color: #dc3545;
}

.error-text {
  font-size: 11px;
}

.codes-container {
  max-width: 200px;
}

.codes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.code-item-container {
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  padding: 4px 6px;
  margin-bottom: 4px;
  cursor: default;
  transition: background-color 0.2s ease;
}

.code-item-container:hover {
  background: #cce7ff;
}

.code-text {
  color: #0056b3;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 11px;
  white-space: nowrap;
  margin-bottom: 2px;
}

.code-timestamp {
  color: #6c757d;
  font-size: 10px;
  font-weight: normal;
  white-space: nowrap;
  direction: ltr;
  text-align: left;
}

.no-codes {
  color: #6c757d;
  font-style: italic;
}

.no-codes-text {
  font-size: 11px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sent-codes-display {
    min-width: 80px;
  }

  .codes-container {
    max-width: 150px;
  }

  .code-item-container {
    padding: 3px 4px;
  }

  .code-text {
    font-size: 10px;
  }

  .code-timestamp {
    font-size: 9px;
  }
}
</style>
